import { CommandCreateFolder } from "./commands/cmdCreateFolder";
import { CommandCreateTemplate } from "./commands/cmdCreateTemplate";
import { CommandDeleteFolder } from "./commands/cmdDeleteFolder";
import { CommandDeleteLayer } from "./commands/cmdDeleteLayer";
import { CommandDeleteTemplate } from "./commands/cmdDeleteTemplate";
import { CommandGetFolders } from "./commands/cmdGetFolders";
import { CommandGetOrganization } from "./commands/cmdGetOrganization";
import { CommandListLayers } from "./commands/cmdListLayers";
import { CommandListTemplates } from "./commands/cmdListTemplates";
import { CommandUpdateFolder } from "./commands/cmdUpdateFolder";
import { CommandUpdateLayer } from "./commands/cmdUpdateLayer";
import { normalizeColor } from "../../shared/utils/common";
import { CommandCreateLayer } from "./commands/cmdCreateLayer";
import { EffectStep } from "./layers/effectStep.model";
import { Effect } from "./layers/effect.model";
import { CommandSetTemplateThumbnail } from "./commands/cmdSetTemplateThumbnail";
import { CommandSetTemplateMedia } from "./commands/cmdSetTemplateMedia";
import { CommandSaveOverrides } from "./commands/cmdSaveOverrides";
import { CommandGetTagGroups } from "./commands/CommandGetTagGroups";
import { CommandGetTags } from "./commands/CommandGetTags";
import { CommandGetTagsLinked } from "./commands/CommandGetTagsLinked";
import { CommandLinkTemplateToTag } from "./commands/CommandLinkTemplateToTag";
import { CommandUnLinkTemplateToTag } from "./commands/CommandUnLinkTemplateToTag";
import { CommandSetTemplateDefault } from "./commands/cmdSetTemplateDefault";
import { CommandSearchContent } from "../../shared/services/contentCatalog/commands/cmdSearchContent";
import { TagGroup } from "../../shared/models/tagGroup.model";
import { CommandCreateTemplateChild } from "./commands/cmdCreateTemplateChild";
import { CommandDupeTemplateAsChild } from "./commands/cmdDupeTemplateAsChild";
import { spawnLayeredMediaRender } from "@/modules/kinema/layeredMediaService";
import { CommandSetTemplateStatic } from "./commands/cmdSetTemplateStatic";
import { CommandUpdateTemplate } from "./commands/cmdUpdateTemplate";
import { CommandUpdateAd } from "./commands/cmdUpdateAd";

export class CreateServiceV3 {
  //new ones
  async getOrganization() {
    const cmd = new CommandGetOrganization();
    const results = await cmd.run();
    return results;
  }

  async getFolders(organizationId: number) {
    const cmd = new CommandGetFolders();
    const results = await cmd.run(organizationId);
    return results;
  }

  async createFolder(organizationId: number, folderName: string) {
    const cmd = new CommandCreateFolder();
    const results = await cmd.run(organizationId, folderName, "folder");
    return results;
  }

  async updateFolder(
    organizationId: number,
    folderId: number,
    folderName: string
  ) {
    const cmd = new CommandUpdateFolder();
    const results = await cmd.run(
      organizationId,
      folderId,
      folderName,
      "folder"
    );
    return results;
  }

  async deleteFolder(organizationId: number, folderId: number) {
    const cmd = new CommandDeleteFolder();
    const results = await cmd.run(organizationId, folderId);
    return results;
  }

  //TEMPLATES
  async createTemplate(folderId: number, templateName: string) {
    const cmd = new CommandCreateTemplate();
    const results = await cmd.run(folderId, templateName);
    return results;
  }

  async createTemplateChild(parentId: number, templateName: string) {
    const cmd = new CommandCreateTemplateChild();
    const results = await cmd.run(parentId, templateName);
    return results;
  }

  async duplicateTemplateAsChild(parentId: number) {
    const cmd = new CommandDupeTemplateAsChild();
    const results = await cmd.run(parentId);
    return results;
  }

  async updateTemplate(templateId: number, patch: any) {
    const cmd = new CommandUpdateTemplate();
    const results = await cmd.run(templateId, patch);
    return results;
  }

  async updateAd(adId: number, patch: any) {
    const cmd = new CommandUpdateAd();
    const results = await cmd.run(adId, patch);
    return results;
  }

  async listTemplates(folderId: number) {
    const cmd = new CommandListTemplates();
    const results = await cmd.run(folderId);
    return results;
  }

  async deleteTemplate(templateId: number) {
    const cmd = new CommandDeleteTemplate();
    const results = await cmd.run(templateId);
    return results;
  }

  async getLayersByTemplateId(templateId: number) {
    const cmd = new CommandListLayers();
    const results = await cmd.run(templateId);
    return results;
  }

  async setTemplateThumbnail(templateId: number, file: File) {
    const cmd = new CommandSetTemplateThumbnail();
    const results = await cmd.run(templateId, file);
    return results;
  }

  async setTemplateMedia(templateId: number, file: File, metadata?: any) {
    const cmd = new CommandSetTemplateMedia();
    const results = await cmd.run(templateId, file, metadata);
    return results;
  }

  createLayerFormData(parentId: string | null, layer: any, files: any) {
    const formData = new FormData();
    formData.append("id", layer.id);
    formData.append("name", layer.name);
    formData.append("order", layer.order);
    formData.append("type", layer.type);
    formData.append("size_w", Math.round(layer.size_w).toString());
    formData.append("size_h", Math.round(layer.size_h).toString());
    formData.append("start_time", layer.start_time);
    formData.append("end_time", layer.end_time);
    formData.append("visible", layer.visible);

    if (layer.type != "composition") {
      if (parentId === null) {
        console.log("Missing parent ID on sub field");
        return formData;
      }
      formData.append("parent_id", parentId);
      const effectsCopy = layer.effects.map((effect: Effect) => {
        const effectCopy: Effect = new Effect({
          ...effect,
          params: { ...effect.params },
          steps: [], // will be filled below
        });

        const updatedSteps = effect.steps.map((step: EffectStep) => {
          const stepCopy = { ...step }; // avoid mutating original
          return stepCopy;
        });

        return {
          ...effectCopy,
          steps: updatedSteps,
        };
      });
      formData.append("effects", JSON.stringify(effectsCopy));
    }
    /*NOTE: this piece of code
                is due a bug with Valtio given that screws the Files due to the proxy, so I create a new File from in a local state and then
                I insert it into the formData to receive the file correctly
            */
    if (files) {
      if (files.source instanceof File) {
        formData.append("source", files.source);
      }
      if (files.compressedSource instanceof File) {
        formData.append("compressed_source", files.compressedSource);
      }
    }

    if (layer.type === "text") {
      formData.append(
        "layer_type_fields",
        JSON.stringify({
          text: layer.text,
          font: layer.font,
          font_size: layer.font_size,
          color: normalizeColor(layer.color),
          margin_x: 0,
          margin_y: 0,
          horizontal_align: layer.horizontal_align,
          text_align: layer.horizontal_align, // MoviePy is weird
          overflow: layer.overflow,
        })
      );
    } else if (layer.type === "video") {
      const videoFields: Record<string, any> = { fps: 30 };
      if (files && typeof files.source === "string") {
        videoFields.source = files.source;
      }
      if (files && typeof files.compressedSource === "string") {
        videoFields.compressed_source = files.compressedSource;
      }
      formData.append("layer_type_fields", JSON.stringify(videoFields));
    } else if (layer.type === "color") {
      formData.append(
        "layer_type_fields",
        JSON.stringify({
          color_value: normalizeColor(layer.color),
          shape_data: layer.shape_data,
        })
      );
    } else if (layer.type === "image") {
      const imageFields: Record<string, any> = { maintain_aspect_ratio: false };
      if (files && typeof files.source === "string") {
        imageFields.source = files.source;
      }
      formData.append("layer_type_fields", JSON.stringify(imageFields));
    }
    return formData;
  }

  async createLayer(
    parentId: string | null,
    templateId: number,
    form: any,
    files: any
  ) {
    const cmd = new CommandCreateLayer();
    const formData = this.createLayerFormData(parentId, form, files);
    const results = await cmd.run(templateId, formData);
    return results;
  }

  async updateLayer(parentId: string | null, form: any, files: any) {
    const cmd = new CommandUpdateLayer();
    const formData = this.createLayerFormData(parentId, form, files);
    const results = await cmd.run(form.id, formData);
    return results;
  }

  async deleteLayer(layerId: string) {
    const cmd = new CommandDeleteLayer();
    const results = await cmd.run(layerId);
    return results;
  }

  async saveOverrides(adId: string, overrides: any[], files: any) {
    const cmd = new CommandSaveOverrides();
    const results = await cmd.run(adId, overrides, files);
    return results;
  }

  async getTagGroups() {
    const cmd = new CommandGetTagGroups();
    const results = await cmd.run();
    return results;
  }

  async getTags(tagGroupId: number) {
    const cmd = new CommandGetTags();
    const results = await cmd.run(tagGroupId);
    return results;
  }

  async getTagsLinkedToTemplate(templateId: number) {
    const cmd = new CommandGetTagsLinked();
    const results = await cmd.run(templateId);
    return results;
  }

  async linkTagsToTemplate(
    templateId: number,
    tagsLinkedToTemplate: TagGroup[]
  ) {
    const cmd = new CommandLinkTemplateToTag();
    const results = await cmd.run(templateId, tagsLinkedToTemplate);
    return results;
  }

  async unLinkTagsFromTemplate(templateId: number, tagsIds: Number[]) {
    const cmd = new CommandUnLinkTemplateToTag();
    const results = await cmd.run(templateId, tagsIds);
    return results;
  }

  async setTemplateDefault(templateId: number, is_default: boolean) {
    const cmd = new CommandSetTemplateDefault();
    const results = await cmd.run(templateId, is_default);
    return results;
  }

  async setTemplateStatic(templateId: number, isStatic: boolean) {
    const cmd = new CommandSetTemplateStatic();
    const results = await cmd.run(templateId, isStatic);
    return results;
  }

  async searchContent(filter: object) {
    const cmd = new CommandSearchContent();
    const results = await cmd.run(filter);
    return results;
  }

  async renderLayer(layerId: number) {
    return spawnLayeredMediaRender(layerId);
  }

}
